import React, { useEffect, useState, useMemo } from "react";
import { runValidators, supportValidators, VALIDATION_STATUS } from "./validators";
import SupportSection from "./SupportSection";

const StatusIcon = ({ status, size = "md" }) => {
  const dim = size === "sm" ? "w-3.5 h-3.5 text-[10px]" : "w-5 h-5 text-xs";
  if (status === VALIDATION_STATUS.SUCCESS) {
    return (
      <span className={`inline-flex items-center justify-center rounded-full bg-green-100 text-green-700 shrink-0 ${dim}`} aria-hidden>
        ✓
      </span>
    );
  }
  if (status === VALIDATION_STATUS.SKIP) {
    return (
      <span className={`inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-500 shrink-0 ${dim}`} aria-hidden>
        —
      </span>
    );
  }
  if (status === VALIDATION_STATUS.WARNING) {
    return (
      <span className={`inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 shrink-0 ${dim}`} aria-hidden>
        !
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 shrink-0 ${dim}`} aria-hidden>
      ✕
    </span>
  );
};

const ValidatorDetailRow = ({ result }) => {
  const { test, status, messages, developerNotes } = result;
  const [expanded, setExpanded] = useState(false);
  const [showDevNotes, setShowDevNotes] = useState(false);

  const messageDevNotes = messages.filter((msg) => msg.developerNotes);
  const hasDeveloperContent =
    Boolean(developerNotes) || messageDevNotes.length > 0 || Boolean(test.id);

  const statusLabel =
    status === VALIDATION_STATUS.WARNING ? "Warning" : "Failed";
  const preview = messages[0]?.message;

  return (
    <div
      className={`border rounded-md shadow-sm ${
        status === VALIDATION_STATUS.WARNING
          ? "border-amber-300 bg-amber-50/60"
          : "border-red-300 bg-red-50/60"
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:opacity-90"
      >
        <StatusIcon status={status} />
        <span className="font-medium text-sm text-gray-900 flex-1 text-left min-w-0 truncate">
          {test.name}
        </span>
        <span className="text-xs text-gray-500 shrink-0">{statusLabel}</span>
        <span className="text-gray-400 text-xs shrink-0">{expanded ? "▼" : "▶"}</span>
      </button>

      {!expanded && preview && (
        <p className="px-3 pb-2 text-xs text-gray-600 truncate">{preview}</p>
      )}

      {expanded && (
        <div className="px-3 pb-2.5 pt-0 border-t border-black/5">
          <ul className="space-y-1.5 pt-2">
            {messages.map((msg, i) => (
              <li
                key={i}
                className={`text-sm text-gray-800 leading-snug pl-2 border-l-2 ${
                  msg.level === VALIDATION_STATUS.ERROR
                    ? "border-red-300"
                    : msg.level === VALIDATION_STATUS.WARNING
                      ? "border-amber-300"
                      : "border-green-300"
                }`}
              >
                {msg.message}
              </li>
            ))}
          </ul>

          {hasDeveloperContent && (
            <div className="mt-2 pt-2 border-t border-black/[0.04]">
              <button
                type="button"
                onClick={() => setShowDevNotes((open) => !open)}
                className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showDevNotes ? "Hide developer notes" : "Show developer notes"}
              </button>

              {showDevNotes && (
                <div className="mt-1.5 rounded border border-gray-200/80 bg-white/60 px-2 py-1.5 space-y-1.5">
                  {messageDevNotes.map((msg, i) => (
                    <pre
                      key={i}
                      className="text-[11px] font-mono text-gray-600 overflow-auto max-h-28 whitespace-pre-wrap leading-relaxed"
                    >
                      {msg.developerNotes}
                    </pre>
                  ))}
                  {developerNotes && (
                    <pre className="text-[11px] font-mono text-gray-600 overflow-auto max-h-28 whitespace-pre-wrap leading-relaxed">
                      {developerNotes}
                    </pre>
                  )}
                  <p className="text-[10px] text-gray-400 font-mono pt-0.5 border-t border-gray-100">
                    {test.id}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompactValidatorGroup = ({ title, count, results, variant }) => {
  const [open, setOpen] = useState(false);

  if (count === 0) return null;

  const isPassed = variant === "passed";
  const chipClass = isPassed
    ? "inline-flex items-center gap-1 text-[11px] leading-tight text-green-800 bg-green-50 border border-green-100 rounded px-1.5 py-0.5"
    : "inline-flex items-center gap-1 text-[11px] leading-tight text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 max-w-full";

  return (
    <div
      className={`border rounded-md shadow-sm ${
        isPassed ? "border-green-200 bg-green-50/40" : "border-gray-300 bg-white"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-black/[0.02]"
      >
        <StatusIcon status={isPassed ? VALIDATION_STATUS.SUCCESS : VALIDATION_STATUS.SKIP} size="sm" />
        <span className={`text-xs font-semibold flex-1 ${isPassed ? "text-green-800" : "text-gray-600"}`}>
          {title} ({count})
        </span>
        <span className="text-[10px] text-gray-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-2.5 pb-2 pt-0 flex flex-wrap gap-1">
          {results.map((result) => {
            const hint = result.messages?.[0]?.message;
            return (
              <span
                key={result.test.id}
                className={chipClass}
                title={hint ? `${result.test.name}: ${hint}` : result.test.name}
              >
                {isPassed ? "✓" : "—"}
                <span className="truncate max-w-[220px]">{result.test.name}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ValidatorResults = ({ validationContext, isRunningValidators }) => {
  const [results, setResults] = useState([]);
  const [runState, setRunState] = useState("idle");

  useEffect(() => {
    if (!validationContext?.patient?.patient_id) {
      setResults([]);
      setRunState("idle");
      return;
    }

    if (validationContext.isLoading) {
      setRunState("waiting");
      return;
    }

    let cancelled = false;
    setRunState("running");

    (async () => {
      const runResults = await runValidators(supportValidators, validationContext);
      if (!cancelled) {
        setResults(runResults);
        setRunState("done");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    validationContext?.patient?.patient_id,
    validationContext?.isLoading,
    validationContext?.doctor,
    validationContext?.transcription,
    validationContext?.summarization,
    validationContext?.trace,
    validationContext?.fetchErrors?.doctor,
    validationContext?.fetchErrors?.transcription,
    validationContext?.fetchErrors?.summarization,
    validationContext?.fetchErrors?.trace,
  ]);

  const grouped = useMemo(() => {
    const passed = [];
    const skipped = [];
    const warnings = [];
    const errors = [];
    for (const r of results) {
      if (r.status === VALIDATION_STATUS.SUCCESS) passed.push(r);
      else if (r.status === VALIDATION_STATUS.SKIP) skipped.push(r);
      else if (r.status === VALIDATION_STATUS.WARNING) warnings.push(r);
      else errors.push(r);
    }
    return { passed, skipped, warnings, errors };
  }, [results]);

  const { passed, skipped, warnings, errors } = grouped;
  const needsAttention = errors.length + warnings.length;

  const collapsedSummary = useMemo(() => {
    if (runState === "waiting" || runState === "running" || isRunningValidators) {
      return "Running checks…";
    }
    if (runState !== "done") return null;
    const parts = [];
    if (passed.length > 0) parts.push(`${passed.length} passed`);
    if (errors.length > 0) parts.push(`${errors.length} failed`);
    if (warnings.length > 0) parts.push(`${warnings.length} warning`);
    if (skipped.length > 0) parts.push(`${skipped.length} skipped`);
    return parts.length > 0 ? parts.join(", ") : "No checks";
  }, [runState, isRunningValidators, passed.length, errors.length, warnings.length, skipped.length]);

  const headerRight =
    runState === "done" ? (
      <div className="flex flex-wrap gap-2 text-[11px]">
        {errors.length > 0 && (
          <span className="text-red-700 font-medium">{errors.length} failed</span>
        )}
        {warnings.length > 0 && (
          <span className="text-amber-700 font-medium">{warnings.length} warning</span>
        )}
        {passed.length > 0 && (
          <span className="text-green-700 font-medium">{passed.length} passed</span>
        )}
      </div>
    ) : null;

  return (
    <SupportSection
      title="Validators"
      summary={collapsedSummary}
      defaultOpen
      headerRight={headerRight}
      contentClassName="px-3 py-2 space-y-1.5"
    >
      {runState === "waiting" && (
        <p className="text-sm text-gray-500 py-1">Waiting for patient, doctor, and job data…</p>
      )}

      {runState === "done" && (
        <>
          {needsAttention > 0 && (
            <div className="space-y-1.5">
              {errors.map((result) => (
                <ValidatorDetailRow key={result.test.id} result={result} />
              ))}
              {warnings.map((result) => (
                <ValidatorDetailRow key={result.test.id} result={result} />
              ))}
            </div>
          )}

          {needsAttention === 0 && passed.length > 0 && (
            <p className="text-xs text-green-700 py-1">All checks passed.</p>
          )}

          <CompactValidatorGroup
            title="Passed"
            count={passed.length}
            results={passed}
            variant="passed"
          />
          <CompactValidatorGroup
            title="Skipped"
            count={skipped.length}
            results={skipped}
            variant="skipped"
          />
        </>
      )}
    </SupportSection>
  );
};

export default ValidatorResults;
